/**
 * BulletPool - Object pool for bullet reuse
 *
 * Performance optimization that pre-allocates bullets and reuses them
 * instead of creating/destroying objects every frame.
 *
 * Benefits:
 * - Eliminates GC pressure from bullet creation
 * - Reduces memory allocation overhead
 * - Keeps Graphics objects alive (no create/destroy)
 */

import Phaser from 'phaser';
import { Bullet, BulletConfig } from '../entities/Bullet';

export interface BulletPoolConfig {
  /** Initial pool size (pre-allocated bullets) */
  initialSize?: number;
  /** Maximum pool size (hard limit) */
  maxSize?: number;
}

export class BulletPool {
  private scene: Phaser.Scene;

  /** All bullets in the pool (both active and inactive) */
  private pool: Bullet[] = [];

  /** Maximum pool size */
  private maxSize: number;

  /** Track pool statistics */
  private stats = {
    created: 0,
    reused: 0,
    peakActive: 0,
  };

  constructor(scene: Phaser.Scene, config?: BulletPoolConfig) {
    this.scene = scene;
    this.maxSize = config?.maxSize ?? 900;

    // Pre-allocate bullets
    const initialSize = config?.initialSize ?? 600;
    this.preallocate(initialSize);
  }

  /**
   * Pre-allocates bullets to avoid runtime allocation
   */
  private preallocate(count: number): void {
    for (let i = 0; i < count; i++) {
      const bullet = this.createBullet();
      this.pool.push(bullet);
    }
  }

  /**
   * Creates a new bullet for the pool (hidden, inactive)
   */
  private createBullet(): Bullet {
    const bullet = new Bullet(this.scene);
    bullet.setPooled(true);
    this.scene.add.existing(bullet);
    this.stats.created++;
    return bullet;
  }

  /**
   * Gets a bullet from the pool (or creates new if pool exhausted)
   * Returns null if at max capacity
   */
  get(config: BulletConfig): Bullet | null {
    // Find an inactive bullet
    let bullet = this.pool.find(b => !b.getIsActive());

    if (!bullet) {
      // Pool exhausted - try to create new if under max
      if (this.pool.length < this.maxSize) {
        bullet = this.createBullet();
        this.pool.push(bullet);
      } else {
        // At max capacity - can't provide bullet
        return null;
      }
    } else {
      this.stats.reused++;
    }

    // Reset and activate the bullet
    bullet.reset(config);

    // Track peak usage
    const activeCount = this.getActiveCount();
    if (activeCount > this.stats.peakActive) {
      this.stats.peakActive = activeCount;
    }

    return bullet;
  }

  /**
   * Releases a bullet back to the pool
   * Note: With current design, bullets auto-release when deactivated
   * This method is mainly for explicit release if needed
   */
  release(bullet: Bullet): void {
    if (bullet.getIsActive()) {
      bullet.deactivate();
    }
  }

  /**
   * Gets all active bullets (for collision detection, updates)
   */
  getActiveBullets(): Bullet[] {
    return this.pool.filter(b => b.getIsActive());
  }

  /**
   * Updates all active bullets
   */
  update(time: number, delta: number): void {
    for (const bullet of this.pool) {
      if (bullet.getIsActive()) {
        bullet.update(time, delta);
      }
    }
  }

  /**
   * Gets count of currently active bullets
   */
  getActiveCount(): number {
    return this.pool.filter(b => b.getIsActive()).length;
  }

  /**
   * Gets total pool size (active + inactive)
   */
  getTotalSize(): number {
    return this.pool.length;
  }

  /**
   * Gets pool utilization as percentage
   */
  getUtilization(): number {
    if (this.pool.length === 0) return 0;
    return (this.getActiveCount() / this.pool.length) * 100;
  }

  /**
   * Gets pool statistics
   */
  getStats(): {
    created: number;
    reused: number;
    peakActive: number;
    currentActive: number;
    totalSize: number;
    utilization: number;
  } {
    return {
      ...this.stats,
      currentActive: this.getActiveCount(),
      totalSize: this.pool.length,
      utilization: this.getUtilization(),
    };
  }

  /**
   * Releases all bullets (clears active state)
   */
  releaseAll(): void {
    for (const bullet of this.pool) {
      if (bullet.getIsActive()) {
        bullet.deactivate();
      }
    }
  }

  /**
   * Cleanup - destroys all bullets in pool
   */
  destroy(): void {
    for (const bullet of this.pool) {
      bullet.destroy();
    }
    this.pool = [];
  }
}
